import { consoleLog } from '@Utils/consoleLog';

import type {
  AddSavedAddressResponse,
  AddSavedCardResponse,
  CancelOrderResponse,
  GetUserOrderHistoryResponse,
  GetUserOrderSummaryResponse,
  GetUserPreferencesResponse,
  GetUserSettingsResponse,
  GetUserSettingsTokenResponse,
  RemoveSavedAddressResponse,
  RemoveSavedCardResponse,
  SendSupportMessageResponse,
  SetDefaultSavedAddressResponse,
  SetDefaultSavedCardResponse,
  SetUserDetailsResponse,
  SetUserPasswordResponse,
  SetUserPrivacyConsentResponse,
  SetUserSmartBrowsingResponse,
  UpdateSavedAddressResponse,
} from './ownable.dtos';
import {
  AddSavedAddress,
  AddSavedCard,
  CancelOrder,
  GetUserOrderHistory,
  GetUserOrderSummary,
  GetUserPreferences,
  GetUserSettings,
  GetUserSettingsToken,
  RemoveSavedAddress,
  RemoveSavedCard,
  SendSupportMessage,
  SetDefaultSavedAddress,
  SetDefaultSavedCard,
  SetUserDetails,
  SetUserPassword,
  SetUserPrivacyConsent,
  SetUserSmartBrowsing,
  UpdateSavedAddress,
} from './ownable.dtos';
import ServiceStackGateway from './service-stack';

import type { UserOrderSummary, UserPrivacyConsentInfo } from '~types/gateways';
import type { Token } from '~types/models-types';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
class UserAPI {
  public static async getUserPreferences() {
    const request = new GetUserPreferences();

    try {
      return await ServiceStackGateway.get<GetUserPreferencesResponse>(request);
    } catch (err) {
      consoleLog({ error: err });
    }
  }

  public static async setUserSmartBrowsing(setting: boolean, warning: boolean) {
    const request = new SetUserSmartBrowsing();
    request.smartBrowsingActive = setting;
    request.smartBrowsingDeactivationWarning = warning;

    try {
      return await ServiceStackGateway.post<SetUserSmartBrowsingResponse>(
        request
      );
    } catch (err) {
      consoleLog({ error: err });
    }
  }

  public static async getUserOrderSummary({
    productId,
    quantity = 1,
    optionId,
  }: UserOrderSummary): Promise<GetUserOrderSummaryResponse> {
    const request = new GetUserOrderSummary();
    request.productId = productId;
    if (optionId) {
      request.optionId = optionId;
    }
    request.quantity = quantity;

    try {
      return await ServiceStackGateway.post<GetUserOrderSummaryResponse>(
        request
      );
    } catch (error) {
      // @ts-expect-error
      return error.responseStatus;
    }
  }

  public static addSavedCard({
    token,
    displayName,
    isDefault = false,
    id,
  }: {
    token: string;
    displayName: string;
    isDefault: boolean;
    id: string;
  }) {
    const request = new AddSavedCard();
    request.token = token;
    request.paymentMethodId = id;
    request.displayName = displayName;
    request.isDefault = isDefault;

    return ServiceStackGateway.post<AddSavedCardResponse>(request);
  }

  public static setDefaultCard(id: number, token: string) {
    const request = new SetDefaultSavedCard();
    request.savedCardId = id;
    request.token = token;

    return ServiceStackGateway.post<SetDefaultSavedCardResponse>(request);
  }

  public static removeSavedCard(token: string, id: number) {
    const request = new RemoveSavedCard();
    request.token = token;
    request.savedCardId = id;

    return ServiceStackGateway.post<RemoveSavedCardResponse>(request);
  }

  public static editUserAddress(
    addressToAdd: any
  ): Promise<UpdateSavedAddressResponse> {
    const {
      token,
      displayName,
      recipientName,
      postCode,
      street,
      county,
      phone,
      townOrCity,
      savedAddressId,
    } = addressToAdd;
    const request = new UpdateSavedAddress();
    request.token = token;
    request.displayName = displayName;
    request.recipientName = recipientName;
    // @ts-expect-error
    request.postcode = postCode;
    request.townOrCity = townOrCity;
    request.street = street;
    request.county = county;
    request.phone = phone;
    request.savedAddressId = savedAddressId;
    return ServiceStackGateway.post<UpdateSavedAddressResponse>(request);
  }

  public static addUserAddress(
    addressToAdd: any
  ): Promise<AddSavedAddressResponse> {
    const {
      token,
      displayName,
      recipientName,
      isDefault,
      postCode,
      street,
      county,
      phone,
      townOrCity,
    } = addressToAdd;
    const request = new AddSavedAddress();
    request.token = token;
    request.displayName = displayName;
    request.recipientName = recipientName;
    request.isDefault = isDefault;
    request.postCode = postCode;
    request.townOrCity = townOrCity;
    request.street = street;
    request.county = county;
    request.phone = phone;

    return ServiceStackGateway.post<AddSavedAddressResponse>(request);
  }

  public static setUserDetails(
    token: string,
    name: string
  ): Promise<SetUserDetailsResponse> {
    const request = new SetUserDetails();
    request.token = token;
    request.fullName = name;

    return ServiceStackGateway.post<SetUserDetailsResponse>(request);
  }

  public static setDefaultSavedAddress(
    token: string,
    addressId: number
  ): Promise<SetDefaultSavedAddressResponse> {
    const request = new SetDefaultSavedAddress();
    request.token = token;
    request.savedAddressId = addressId;

    return ServiceStackGateway.post<SetDefaultSavedAddressResponse>(request);
  }

  public static removeSavedAddress(
    token: string,
    addressId: number
  ): Promise<SetDefaultSavedAddressResponse> {
    const request = new RemoveSavedAddress();
    request.token = token;
    request.savedAddressId = addressId;

    return ServiceStackGateway.post<RemoveSavedAddressResponse>(request);
  }

  public static async getOrderHistory(searchTerm: string) {
    const request = new GetUserOrderHistory();
    request.searchTerm = searchTerm;
    try {
      return await ServiceStackGateway.get<GetUserOrderHistoryResponse>(
        request
      );
    } catch (err) {
      consoleLog(err);
    }
  }

  public static cancelOrderInHistory(id: number) {
    const request = new CancelOrder();
    request.orderId = id;
    return ServiceStackGateway.post<CancelOrderResponse>(request);
  }

  public static async getUserSettingsToken(
    password: string
  ): Promise<Token | null> {
    const request = new GetUserSettingsToken();
    request.password = password;

    try {
      return await ServiceStackGateway.post<GetUserSettingsTokenResponse>(
        request
      );
    } catch (err) {
      if (__DEV__) {
        consoleLog('error getting user settings token', err);
      }
      return null;
    }
  }

  public static async updateUserPassword(
    token: string,
    password: string,
    confirmPassword: string
  ): Promise<{ success: boolean }> {
    const request = new SetUserPassword();
    request.password = password;
    request.confirmPassword = confirmPassword;
    request.token = token;

    try {
      return await ServiceStackGateway.post<SetUserPasswordResponse>(request);
    } catch (err) {
      consoleLog('error updating password', err);
      return { success: false };
    }
  }
  public static async getUserSettings(
    token: string
  ): Promise<GetUserSettingsResponse | null> {
    const request = new GetUserSettings();
    request.token = token;
    try {
      return await ServiceStackGateway.post<GetUserSettingsResponse>(request);
    } catch (err) {
      if (__DEV__) {
        consoleLog(err);
      }
      return null;
    }
  }

  public static async setUserPrivacyConsent({
    enable,
    token,
    onBeforeRequest = () => {
      /* do nothing */
    },
    onRequestComplete = () => {
      /* do nothing */
    },
    onRequestError = () => {
      /* do nothing */
    },
  }: UserPrivacyConsentInfo) {
    onBeforeRequest();
    const request = new SetUserPrivacyConsent();
    request.token = token;
    request.privacyConsent = enable;
    try {
      const response =
        await ServiceStackGateway.post<SetUserPrivacyConsentResponse>(request);
      onRequestComplete(response);
      return response;
    } catch (err) {
      onRequestError(err);
    }
  }

  public static async sendSupportMessage(subject: string, message: string) {
    const request = new SendSupportMessage();
    request.subject = subject;
    request.message = message;
    try {
      return await ServiceStackGateway.post<SendSupportMessageResponse>(
        request
      );
    } catch (err) {
      consoleLog('error sending support message', err);
    }
  }
}

export default UserAPI;
